import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  applyNodeChanges,
  type NodeChange,
  type ReactFlowInstance,
  type XYPosition
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Bot,
  Box,
  BrainCircuit,
  KeyRound,
  LogOut,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  Route,
  Search,
  SearchX,
  Workflow,
  X
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorState, LoadingState } from "../components/AsyncState";
import { useAuth } from "../features/auth/AuthContext";
import { ControlNode } from "../features/canvas/ControlNode";
import { Inspector } from "../features/canvas/Inspector";
import { countLabel } from "../features/canvas/labels";
import { createKinds, nodeRegistry } from "../features/canvas/nodeRegistry";
import { projectCanvas } from "../features/canvas/projection";
import type {
  CanvasKind,
  CanvasSelection,
  ControlCanvasNode,
  DraftKind
} from "../features/canvas/types";
import { apiRequest } from "../lib/api";
import {
  applicationListSchema,
  fabricResourceListSchema,
  healthSchema,
  keyListSchema,
  normalizeState,
  profileListSchema,
  taskListSchema,
  usageSchema,
  virtualModelListSchema
} from "../lib/schemas";

const POSITION_KEY = "synapse.canvas.positions.v1";
const nodeTypes = { control: ControlNode };

function readPositions(): Record<string, XYPosition> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(POSITION_KEY) ?? "{}");
    if (!value || typeof value !== "object") return {};
    return Object.fromEntries(
      Object.entries(value).filter((entry): entry is [string, XYPosition] => {
        const position = entry[1] as Partial<XYPosition>;
        return Number.isFinite(position.x) && Number.isFinite(position.y);
      })
    );
  } catch {
    return {};
  }
}

export function ControlCanvasPage() {
  const client = useQueryClient();
  const { signOut } = useAuth();
  const [positions, setPositions] = useState(readPositions);
  const [nodes, setNodes] = useState<ControlCanvasNode[]>([]);
  const [selection, setSelection] = useState<CanvasSelection>(null);
  const [query, setQuery] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(
    () => window.matchMedia("(min-width: 481px)").matches
  );
  const [flow, setFlow] = useState<ReactFlowInstance<ControlCanvasNode> | null>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  const applications = useQuery({
    queryKey: ["applications"],
    queryFn: () => apiRequest("/v1/admin/applications", applicationListSchema)
  });
  const applicationId = applications.data?.data[0]?.id ?? "";
  const resources = useQuery({
    queryKey: ["resources"],
    queryFn: () => apiRequest("/v1/admin/fabric/resources", fabricResourceListSchema),
    refetchInterval: 15_000
  });
  const profiles = useQuery({
    queryKey: ["profiles"],
    queryFn: () => apiRequest("/v1/admin/fabric/profiles", profileListSchema)
  });
  const virtualModels = useQuery({
    queryKey: ["virtual-models"],
    queryFn: () => apiRequest("/v1/admin/fabric/models", virtualModelListSchema)
  });
  const keys = useQuery({
    queryKey: ["keys", applicationId],
    queryFn: () => apiRequest(`/v1/admin/applications/${applicationId}/keys`, keyListSchema),
    enabled: Boolean(applicationId)
  });
  const tasks = useQuery({
    queryKey: ["tasks"],
    queryFn: () => apiRequest("/v1/admin/tasks?limit=100", taskListSchema),
    refetchInterval: 5_000
  });
  const health = useQuery({
    queryKey: ["ready"],
    queryFn: () => apiRequest("/readyz", healthSchema),
    refetchInterval: 15_000
  });
  const usage = useQuery({
    queryKey: ["usage", applicationId],
    queryFn: () => apiRequest(`/v1/admin/applications/${applicationId}/usage`, usageSchema),
    enabled: Boolean(applicationId),
    refetchInterval: 15_000
  });

  const projection = useMemo(
    () =>
      projectCanvas({
        resources: resources.data?.data ?? [],
        virtualModels: virtualModels.data?.data ?? [],
        profiles: profiles.data?.data ?? [],
        keys: keys.data?.data ?? [],
        tasks: tasks.data?.data ?? [],
        positions,
        compact: window.matchMedia("(max-width: 480px)").matches
      }),
    [keys.data, positions, profiles.data, resources.data, tasks.data, virtualModels.data]
  );

  useEffect(() => setNodes(projection.nodes), [projection.nodes]);
  useEffect(() => {
    if (!flow || nodes.length === 0) return;
    const frame = requestAnimationFrame(() => {
      void flow.fitView({ padding: 0.22, maxZoom: 1, duration: 180 });
    });
    return () => cancelAnimationFrame(frame);
  }, [flow, nodes.length, query]);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const isEditing =
        target?.matches("input, textarea, select") === true || target?.isContentEditable === true;
      if (event.key === "/" && !isEditing) {
        event.preventDefault();
        searchInput.current?.focus();
      }
      if (event.key === "Escape") {
        if (query) setQuery("");
        else if (selection) setSelection(null);
        else if (paletteOpen) setPaletteOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [paletteOpen, query, selection]);

  const normalizedQuery = query.trim().toLowerCase();
  const agentCount = profiles.data?.data.length ?? 0;
  const visibleNodes = normalizedQuery
    ? nodes.filter((node) =>
        `${node.data.title} ${node.data.subtitle} ${node.data.kind} ${node.data.status}`
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : nodes;
  const visibleIds = new Set(visibleNodes.map((node) => node.id));
  const visibleEdges = projection.edges.filter(
    (edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)
  );
  const error =
    resources.error ??
    virtualModels.error ??
    profiles.error ??
    keys.error ??
    tasks.error ??
    applications.error;
  const loading = [resources, virtualModels, profiles, keys, tasks, applications].some(
    (result) => result.isLoading
  );

  function changeNodes(changes: NodeChange<ControlCanvasNode>[]) {
    setNodes((current) => applyNodeChanges(changes, current));
  }
  function persistNodePosition(node: ControlCanvasNode) {
    const next = { ...positions, [node.id]: node.position };
    setPositions(next);
    localStorage.setItem(POSITION_KEY, JSON.stringify(next));
  }
  async function refresh() {
    await client.invalidateQueries();
  }
  function create(kind: DraftKind, source?: ControlCanvasNode) {
    setSelection(source ? { mode: "create", kind, source } : { mode: "create", kind });
    if (window.matchMedia("(max-width: 820px)").matches) setPaletteOpen(false);
  }
  function closeOverlay() {
    if (selection) setSelection(null);
    else setPaletteOpen(false);
  }

  return (
    <main className="canvas-workbench">
      <header className="canvas-commandbar">
        <div className="canvas-brand">
          <span>
            <BrainCircuit size={18} />
          </span>
          <strong>Synapse</strong>
          <small>Agent gateway</small>
        </div>
        <button
          className="icon-button palette-toggle"
          type="button"
          title={paletteOpen ? "Hide node palette" : "Show node palette"}
          onClick={() => setPaletteOpen((value) => !value)}
        >
          {paletteOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
        <label className="canvas-search">
          <Search size={15} aria-hidden="true" />
          <input
            ref={searchInput}
            aria-label="Search topology"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search models, tools, agents"
          />
          {query && (
            <button type="button" title="Clear search" onClick={() => setQuery("")}>
              <X size={14} />
            </button>
          )}
        </label>
        <div className="ai-protocol-fabric" aria-label="Agent protocol fabric">
          <span>
            <BrainCircuit size={13} /> LLM
          </span>
          <i />
          <span>
            <Box size={13} /> MCP
          </span>
          <i />
          <span>
            <Network size={13} /> A2A
          </span>
        </div>
        <span className={`gateway-state ${health.data?.status === "ready" ? "is-ready" : ""}`}>
          <i />
          Runtime {health.data?.status ?? "checking"}
        </span>
        <button
          className="icon-button"
          type="button"
          title="Refresh topology"
          onClick={() => void refresh()}
        >
          <RefreshCw size={16} />
        </button>
        <button className="icon-button" type="button" title="Disconnect" onClick={signOut}>
          <LogOut size={16} />
        </button>
      </header>

      <div className="canvas-stage">
        {(paletteOpen || selection) && (
          <button
            className="canvas-scrim"
            type="button"
            aria-label="Close open panel"
            onClick={closeOverlay}
          />
        )}
        {paletteOpen && <NodePalette onCreate={create} />}
        {!paletteOpen && (
          <button
            className="palette-fab icon-button"
            type="button"
            title="Open node palette"
            onClick={() => setPaletteOpen(true)}
          >
            <Plus size={16} />
          </button>
        )}
        <section className="canvas-surface" aria-label="Gateway topology canvas">
          {loading && (
            <div className="canvas-overlay">
              <LoadingState label="Loading agent fabric" />
            </div>
          )}
          {error instanceof Error && (
            <div className="canvas-error">
              <ErrorState error={error} />
            </div>
          )}
          <ReactFlow<ControlCanvasNode>
            nodes={visibleNodes}
            edges={visibleEdges}
            nodeTypes={nodeTypes}
            onNodesChange={changeNodes}
            onNodeDragStop={(_, node) => persistNodePosition(node)}
            onNodeClick={(_, node) => setSelection({ mode: "resource", node })}
            onPaneClick={() => setSelection(null)}
            onInit={setFlow}
            fitView
            fitViewOptions={{ padding: 0.22, maxZoom: 1 }}
            minZoom={0.3}
            maxZoom={1.65}
            nodesConnectable={false}
            deleteKeyCode={null}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#aeb8c0" gap={24} size={1} variant={BackgroundVariant.Dots} />
            <Controls showInteractive={false} position="bottom-left" />
            <MiniMap
              nodeColor={(node) => nodeRegistry[node.data.kind as CanvasKind].color}
              maskColor="rgba(244,246,248,.78)"
              position="bottom-right"
              pannable
              zoomable
            />
          </ReactFlow>
          {nodes.length === 0 && !loading && !error && (
            <div className="canvas-empty">
              <BrainCircuit size={25} />
              <strong>No Agent resources</strong>
              <button
                className="primary-button compact"
                type="button"
                onClick={() => create("llm")}
              >
                <Plus size={14} /> Add channel
              </button>
            </div>
          )}
          {nodes.length > 0 && visibleNodes.length === 0 && (
            <div className="canvas-empty canvas-no-results">
              <SearchX size={25} />
              <strong>No matching nodes</strong>
              <button className="quiet-button compact" type="button" onClick={() => setQuery("")}>
                Clear search
              </button>
            </div>
          )}
          <button
            className="fit-button quiet-button compact"
            type="button"
            onClick={() => void flow?.fitView({ padding: 0.2, duration: 250 })}
          >
            Fit topology
          </button>
        </section>
        <Inspector selection={selection} onClose={() => setSelection(null)} onCreate={create} />
      </div>
      <footer className="canvas-statusbar">
        <span>
          <i
            className={`status-dot ${resources.data?.data.some((item) => item.active_revision > 0) ? "online" : ""}`}
          />
          {countLabel(resources.data?.data.length ?? 0, "resource")}
        </span>
        <span>{countLabel(agentCount, "Agent")}</span>
        <span>{countLabel(virtualModels.data?.data.length ?? 0, "virtual model")}</span>
        {normalizedQuery && (
          <span className="search-result-count">
            {String(visibleNodes.length)} / {String(nodes.length)} visible
          </span>
        )}
        <span className="active-task-count">
          {countLabel(
            tasks.data?.data.filter((task) =>
              ["running", "waiting_approval"].includes(normalizeState(task.status))
            ).length ?? 0,
            "active task"
          )}
        </span>
        <span className="pending-discovery-count">
          {countLabel(
            projection.nodes.filter((node) => node.data.kind === "discovery").length,
            "pending candidate"
          )}
        </span>
        <span className="status-cost">
          ${((usage.data?.cost_microunits ?? 0) / 1_000_000).toFixed(4)} tracked
        </span>
      </footer>
    </main>
  );
}

const paletteIcons = {
  llm: BrainCircuit,
  mcp: Box,
  a2a: Network,
  model: Route,
  agent: Bot,
  key: KeyRound,
  task: Workflow
};
function NodePalette({ onCreate }: { onCreate: (kind: DraftKind) => void }) {
  return (
    <aside className="node-palette" aria-label="Node palette">
      <div className="palette-heading">
        <span>Agent stack</span>
        <small>Models, tools, delegation</small>
      </div>
      <div className="palette-list">
        {createKinds.map((kind) => {
          const Icon = paletteIcons[kind];
          return (
            <button
              type="button"
              key={kind}
              onClick={() => onCreate(kind)}
              style={{ "--node-accent": nodeRegistry[kind].color } as React.CSSProperties}
            >
              <span>
                <Icon size={15} />
              </span>
              <strong>{nodeRegistry[kind].label}</strong>
              <Plus size={14} />
            </button>
          );
        })}
      </div>
      <div className="palette-key">
        <span>
          <i className="port input" /> accepts
        </span>
        <span>
          <i className="port output" /> derives
        </span>
      </div>
    </aside>
  );
}
